/**
 * Record sections of a table (UD-SECTIONS): flat named dividers that group a table's RECORDS
 * (not the workspace sections). The server owns membership, order, counts and paging: a section's
 * records are an ordinary records query scoped by `section=<id | none>`, the counts come from
 * `/sections`, and nothing here partitions a loaded page. Every query lives under the table's
 * records key, so edits, creations and the realtime `records` hint refresh it with the grid.
 */
import {
  type InfiniteData,
  keepPreviousData,
  type QueryClient,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useState } from 'react';

import { api, queryKeys } from '@/shared/api';

import { recordParams } from './api';
import {
  type DatabaseRecord,
  type DatabaseSection,
  NO_SECTION,
  type RecordFilter,
  type RecordPage,
  type RecordQuery,
  type SectionList,
} from './types';

const PAGE_SIZE = 100;
/** Local (per-section) filters, as the section whose records they narrow -> its conditions. */
export type SectionFilters = Readonly<Record<string, readonly RecordFilter[]>>;

const sectionsKey = (tableId: string, ...rest: unknown[]) =>
  [...queryKeys.databases.recordsAll(tableId), 'sections', ...rest] as const;
const pagesKey = (tableId: string, ...rest: unknown[]) =>
  [...queryKeys.databases.recordsAll(tableId), 'section-pages', ...rest] as const;

const conditionOf = (f: RecordFilter) =>
  f.value === undefined ? `${f.column_id}:${f.op}` : `${f.column_id}:${f.op}:${f.value}`;

/** `section_filter` items: `<section id | none>/<column_id>:<op>[:<value>]`. */
export function sectionFilterParams(local: SectionFilters): string[] {
  return Object.entries(local).flatMap(([section, filters]) =>
    filters.map((f) => `${section}/${conditionOf(f)}`),
  );
}

/** The sections of the table in order with their record counts (total and matching). */
export function useSections(
  tableId: string,
  query: RecordQuery,
  local: SectionFilters,
  enabled = true,
) {
  const { q, filters } = query;
  return useQuery({
    enabled,
    queryKey: sectionsKey(tableId, q, filters, local),
    queryFn: ({ signal }) => {
      const { query: params, repeat } = recordParams({ q, filters, sort: [] });
      const own = sectionFilterParams(local);
      return api.get<SectionList>(`/v1/database-tables/${tableId}/sections`, {
        signal,
        query: params,
        repeat: { ...repeat, ...(own.length > 0 && { section_filter: own }) },
      });
    },
    placeholderData: keepPreviousData,
  });
}

/** What to load for one band: the section's key and its own filter (`enabled`: not collapsed). */
export interface SectionSpec {
  key: string;
  filters: readonly RecordFilter[];
  enabled: boolean;
}

/** One band's records as loaded so far (the pages asked for, in the query's order). */
export interface SectionSlice {
  key: string;
  items: DatabaseRecord[];
  /** Matching records of the section (first page's `total`). */
  total: number | null;
  hasMore: boolean;
  /** Fetching with nothing to show yet. */
  pending: boolean;
  fetching: boolean;
  error: unknown;
}

/**
 * The records of several sections, each an ordinary scoped query (filters + search + sort of the
 * table, the section's own filter ANDed). A band shows as many pages as were asked for: `loadMore`
 * raises that number and refetches the SAME query, so the list under the reader stays put while the
 * next page arrives (and a refresh after a change re-reads the same number of pages, like an
 * infinite query). The data has the shape of an infinite query's, so optimistic cell edits and
 * refreshes treat it like any other loaded page.
 */
export function useSectionRecords(
  tableId: string,
  query: RecordQuery,
  specs: readonly SectionSpec[],
): { slices: SectionSlice[]; loadMore: (key: string) => void } {
  const client = useQueryClient();
  // how many pages each (section, query) shows; read by the query function when it runs
  const [wanted] = useState(() => new Map<string, number>());
  const fullOf = (spec: SectionSpec): RecordQuery => ({
    ...query,
    filters: [...query.filters, ...spec.filters],
  });
  const results = useQueries({
    queries: specs.map((spec) => {
      const full = fullOf(spec);
      const id = JSON.stringify([tableId, spec.key, full]);
      return {
        enabled: spec.enabled,
        queryKey: pagesKey(tableId, spec.key, full),
        queryFn: async ({ signal }: { signal: AbortSignal }) => {
          const { query: params, repeat } = recordParams(full);
          const pages: RecordPage[] = [];
          const pageParams: (string | null)[] = [];
          let cursor: string | null = null;
          for (let index = 0; index < (wanted.get(id) ?? 1); index++) {
            const page: RecordPage = await api.get<RecordPage>(
              `/v1/database-tables/${tableId}/records`,
              {
                signal,
                query: { ...params, section: spec.key, limit: PAGE_SIZE, cursor },
                repeat,
              },
            );
            pages.push(page);
            pageParams.push(cursor);
            if (!page.next_cursor) break;
            cursor = page.next_cursor;
          }
          return { pages, pageParams } satisfies InfiniteData<RecordPage, string | null>;
        },
        placeholderData: keepPreviousData,
      };
    }),
  });
  const loadMore = (key: string) => {
    const spec = specs.find((s) => s.key === key);
    if (!spec) return;
    const full = fullOf(spec);
    const id = JSON.stringify([tableId, key, full]);
    wanted.set(id, (wanted.get(id) ?? 1) + 1);
    void client.refetchQueries({ queryKey: pagesKey(tableId, key, full), exact: true });
  };
  const slices = results.map((result, index) => {
    const pages = result.data?.pages ?? [];
    const last = pages[pages.length - 1];
    return {
      key: specs[index]?.key ?? NO_SECTION,
      items: pages.flatMap((page) => page.items),
      total: pages[0]?.total ?? null,
      hasMore: last?.next_cursor != null,
      pending: result.isPending && result.fetchStatus !== 'idle',
      fetching: result.isFetching,
      error: result.error,
    };
  });
  return { slices, loadMore };
}

function refresh(client: QueryClient, tableId: string) {
  return client.invalidateQueries({ queryKey: queryKeys.databases.recordsAll(tableId) });
}

export interface SectionInput {
  name: string;
  color?: string | null;
  /** The section STARTS at this record (it takes the rest of the record's section). */
  before_record_id?: string;
  after_section_id?: string;
  before_section_id?: string;
}

export function useCreateSection(tableId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: SectionInput) =>
      api.post<DatabaseSection>(`/v1/database-tables/${tableId}/sections`, body),
    onSettled: () => refresh(client, tableId),
  });
}

export function useUpdateSection(tableId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...patch
    }: {
      id: string;
      name?: string;
      color?: string | null;
      expected_version?: number;
    }) => api.patch<DatabaseSection>(`/v1/database-record-sections/${id}`, patch),
    onSettled: () => refresh(client, tableId),
  });
}

export function useMoveSection(tableId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...anchor }: { id: string; after_id?: string; before_id?: string }) =>
      api.post<DatabaseSection>(`/v1/database-record-sections/${id}/move`, anchor),
    onSettled: () => refresh(client, tableId),
  });
}

/** Deletes the section only: its records go to "No section". */
export function useDeleteSection(tableId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/v1/database-record-sections/${id}`),
    onSettled: () => refresh(client, tableId),
  });
}

/** Puts exactly these records into a section (`null` = "No section"); all or nothing. */
export function useSetRecordsSection(tableId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { record_ids: readonly string[]; section_id: string | null }) =>
      api.post<string[]>(`/v1/database-tables/${tableId}/records/section`, input),
    onSettled: () => refresh(client, tableId),
  });
}
